import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import Aviso from './Aviso';
import Carregando from './Carregando';
import {
  enfileirarImpressao, lerItensDaVenda, lerVendas, quitacaoDaVenda, type VendaDetalhada,
} from '../lib/consultas';
import type { FiadoRow } from '@core/types';
import { chavesDasPessoas } from '../lib/fiado';
import {
  cobra, extratoParaImprimirAqui, montarExtrato, montarPdfFiado, nomeDoArquivoFiado,
  paginasDoExtratoHtml, rotuloDaSituacao, vendaNoExtrato, type VendaNoExtrato,
} from '../lib/fiadoDoc';
import { imprimirAqui, textoDoCarro } from '../lib/reciboDoc';
import { ehItemAvulso } from '../lib/coringa';
import {
  baixarArquivo, compartilharArquivos, podeCompartilhar, type ResultadoDoEnvio,
} from '../lib/compartilhar';
import { brl, dataBR } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';

// =============================================================================
// EMITIR O FIADO (14/10/2026) — pedido do dono: "Preciso enviar para uma
// oficina o que eles devem, mas também preciso imprimir na loja para
// assinarmos em conjunto com o cliente quando ele está aqui".
//
// Mora na tela de Vendas e aparece quando o filtro "Fiado" está ligado: as
// vendas que entram são as que a LISTA está mostrando (período, vendedor e
// busca), agrupadas por pessoa pela mesma régua da tela do Fiado
// (chavesDasPessoas: o cadastro manda; sem cadastro, o nome). O saldo de cada
// venda é o do banco (quitacao_da_venda), lido na hora de abrir a pessoa.
//
// Dois destinos, como o vale e o orçamento:
//   * Imprimir na loja — pela fila, 2 vias (cliente e loja) com as linhas de
//     assinatura. Uma folha A4 por trabalho: o agente imprime uma por vez.
//   * PDF — Enviar (o Compartilhar do celular → WhatsApp da oficina) ou
//     Baixar. O arquivo fica PRONTO antes do toque: o iPhone só abre o
//     Compartilhar dentro do gesto (ver lib/compartilhar).
// =============================================================================

interface Pessoa {
  chave: string;
  nome: string;
  vendas: VendaDetalhada[];
  total: number;
}

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/** As pessoas das vendas fiadas da tela, da que mais comprou para a que menos. */
export function pessoasDasVendasFiadas(vendas: VendaDetalhada[]): Pessoa[] {
  const fiadas = vendas.filter((v) => v.forma_pagamento === 'fiado' && !v.cancelada_em);
  const chaves = chavesDasPessoas(fiadas.map((v) => ({
    id: v.id, cliente: v.cliente?.trim() || 'Sem nome', customer_id: v.customer_id,
  })));
  const grupos = new Map<string, Pessoa>();
  for (const v of fiadas) {
    const chave = chaves.get(v.id)!;
    const g = grupos.get(chave) ?? { chave, nome: v.cliente?.trim() || 'Sem nome', vendas: [], total: 0 };
    g.vendas.push(v);
    g.total += Number(v.total_cobrado ?? v.total);
    grupos.set(chave, g);
  }
  return [...grupos.values()].sort((a, b) => b.total - a.total || a.nome.localeCompare(b.nome));
}

export default function EmitirFiado({
  vendas, gerencia, emitidoPor, periodo, aoAmpliarPeriodo, aoFechar,
}: {
  vendas: VendaDetalhada[];
  gerencia: boolean;
  emitidoPor: string | null;
  /** O período da lista, para o painel dizer de onde as vendas vieram. */
  periodo: { de: string; ate: string };
  /** Abre a lista para trás — conta de oficina costuma ter meses. */
  aoAmpliarPeriodo?: () => void;
  aoFechar: () => void;
}) {
  const pessoas = useMemo(() => pessoasDasVendasFiadas(vendas), [vendas]);
  const [escolhida, setEscolhida] = useState<string | null>(null);
  // Uma pessoa só na lista (a busca já achou a oficina): ela vem escolhida.
  const chave = escolhida ?? (pessoas.length === 1 ? pessoas[0]!.chave : null);
  const pessoa = pessoas.find((p) => p.chave === chave) ?? null;

  return (
    <section className="cartao space-y-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="rotulo">Emitir o fiado</h2>
        <button type="button" className="text-xs text-dim underline" onClick={aoFechar}>fechar</button>
      </div>
      <p className="text-xs text-mute">
        Sai o extrato do que a pessoa deve, venda por venda: para <strong>imprimir na loja</strong> e
        assinar junto com o cliente, ou em <strong>PDF</strong> para mandar à oficina. Entram as vendas
        fiadas da lista abaixo — de {dataBR(`${periodo.de}T12:00:00`)} a {dataBR(`${periodo.ate}T12:00:00`)}.
        {aoAmpliarPeriodo && (
          <>
            {' '}
            <button type="button" className="font-bold underline" onClick={aoAmpliarPeriodo}>
              Pegar desde o começo do sistema
            </button>
          </>
        )}
      </p>

      {pessoas.length === 0 ? (
        <Aviso tipo="info">Nenhuma venda fiada na lista. Amplie o período ou limpe a busca.</Aviso>
      ) : (
        <div>
          <label className="rotulo" htmlFor="emitir-pessoa">De quem</label>
          <select id="emitir-pessoa" className="campo" value={chave ?? ''}
                  onChange={(e) => setEscolhida(e.target.value || null)}>
            {pessoas.length > 1 && <option value="">Escolha o cliente…</option>}
            {pessoas.map((p) => (
              <option key={p.chave} value={p.chave}>
                {p.nome} — {plural(p.vendas.length, 'venda', 'vendas')}
              </option>
            ))}
          </select>
        </div>
      )}

      {pessoa && (
        <ExtratoDaPessoa key={pessoa.chave} pessoa={pessoa} gerencia={gerencia} emitidoPor={emitidoPor} />
      )}
    </section>
  );
}

/**
 * O MESMO painel, aberto de dentro da aba Fiado (14/10/2026): lá a pessoa já
 * está escolhida e o que se sabe dela são as CONTAS (listar_fiados), que não
 * trazem o código do recibo nem o carro. Então as vendas são buscadas pela
 * porta da lista de Vendas de quem olha (dono: a tabela; gerente: a RPC da
 * gerência), no intervalo das contas dela, e filtradas pelos ids. Nada novo no
 * banco: as mesmas leituras que a aba Vendas já faz.
 *
 * O intervalo é o das contas EM ABERTO (é o que vai para o papel); sem
 * nenhuma aberta, o de todas — senão o painel não teria o que mostrar.
 */
export function EmitirFiadoDaPessoa({ nome, contas, verFaturamento, emitidoPor, aoFechar }: {
  nome: string;
  contas: FiadoRow[];
  verFaturamento: boolean;
  emitidoPor: string | null;
  aoFechar: () => void;
}) {
  const abertas = contas.filter((c) => !c.perdido_em && Number(c.saldo) > 0);
  const alvo = abertas.length ? abertas : contas;
  const ids = alvo.map((c) => c.sale_id);
  const vendas = useQuery({
    queryKey: ['vendas-do-fiado', verFaturamento, ids.join(',')],
    staleTime: 0, gcTime: 0, refetchOnMount: 'always',
    queryFn: async () => {
      const dias = alvo.map((c) => new Date(c.vendida_em).getTime());
      // Um dia de folga nas pontas: o corte é por instante, e fuso não morde.
      const de = new Date(Math.min(...dias) - 86_400_000).toISOString();
      const ate = new Date(Math.max(...dias) + 86_400_000).toISOString();
      const todas = await lerVendas(true, verFaturamento)({ de, ate });
      const querem = new Set(ids);
      return todas.filter((v) => querem.has(v.id));
    },
    enabled: ids.length > 0,
  });

  const lista = vendas.data ?? [];
  const faltam = ids.length - lista.length;
  return (
    <section className="space-y-3 border border-edge p-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="rotulo">Emitir o fiado de {nome}</h3>
        <button type="button" className="text-xs text-dim underline" onClick={aoFechar}>fechar</button>
      </div>
      <p className="text-xs text-mute">
        O extrato do que a pessoa deve, venda por venda: <strong>imprimir na loja</strong> para
        assinar junto com o cliente, ou em <strong>PDF</strong> para mandar.
      </p>
      {vendas.isPending && ids.length > 0 ? <Carregando texto="Buscando as vendas" /> : null}
      {vendas.error ? <Aviso tipo="erro">{mensagemDeErro(vendas.error)}</Aviso> : null}
      {vendas.data && faltam > 0 && (
        <Aviso tipo="alerta">
          {plural(faltam, 'venda não foi encontrada', 'vendas não foram encontradas')} e fica fora do
          extrato. Confira na tela de Vendas antes de cobrar.
        </Aviso>
      )}
      {vendas.data && lista.length > 0 && (
        <ExtratoDaPessoa
          pessoa={{ chave: nome, nome, vendas: lista, total: 0 }}
          gerencia
          emitidoPor={emitidoPor}
        />
      )}
    </section>
  );
}

function ExtratoDaPessoa({ pessoa, gerencia, emitidoPor }: {
  pessoa: Pessoa; gerencia: boolean; emitidoPor: string | null;
}) {
  // Sempre fresco: alguém pode ter acertado uma conta agora há pouco, e o
  // papel assinado com o valor velho é briga de balcão.
  const detalhes = useQuery({
    queryKey: ['extrato-fiado', gerencia, pessoa.vendas.map((v) => v.id).join(',')],
    staleTime: 0, gcTime: 0, refetchOnMount: 'always',
    queryFn: async (): Promise<VendaNoExtrato[]> => Promise.all(pessoa.vendas.map(async (v) => {
      const [itens, quitacao] = await Promise.all([
        lerItensDaVenda(gerencia)(v.id),
        quitacaoDaVenda(v.id),
      ]);
      return vendaNoExtrato(
        { ...v, veiculo: textoDoCarro(v.veiculo_montadora, v.veiculo_modelo, v.veiculo_ano) },
        itens.map((i) => ({
          id: i.id,
          // A peça diversa sai pelo nome que o vendedor declarou (como no recibo).
          descricao: ehItemAvulso(i) ? (i.descricao_avulsa as string) : i.products?.descricao ?? 'peça',
          quantidade: i.quantidade,
          subtotal: i.subtotal,
        })),
        quitacao,
      );
    })),
  });

  // null = o padrão: só as que ainda cobram.
  const [marcadas, setMarcadas] = useState<Set<string> | null>(null);
  const [duasVias, setDuasVias] = useState(true);
  const [assinatura, setAssinatura] = useState(true);
  const [resultado, setResultado] = useState<ResultadoDoEnvio | null>(null);
  const [enviando, setEnviando] = useState(false);

  const todas = useMemo(() => detalhes.data ?? [], [detalhes.data]);
  const escolhidas = useMemo(
    () => marcadas ?? new Set(todas.filter(cobra).map((v) => v.saleId)),
    [marcadas, todas],
  );
  const alternar = (id: string) => {
    const nova = new Set(escolhidas);
    if (nova.has(id)) nova.delete(id); else nova.add(id);
    setMarcadas(nova);
    setResultado(null);
  };

  // A hora do extrato é a dos DADOS, não a do toque.
  const feitoEm = detalhes.dataUpdatedAt;
  const extrato = useMemo(() => (detalhes.data
    ? montarExtrato(pessoa.nome, detalhes.data.filter((v) => escolhidas.has(v.saleId)), {
      emitidoEm: new Date(feitoEm).toISOString(), emitidoPor,
    })
    : null), [detalhes.data, escolhidas, pessoa.nome, feitoEm, emitidoPor]);
  const pdf = useMemo(() => (extrato && extrato.vendas.length
    ? new File([montarPdfFiado(extrato, { assinatura }) as BlobPart], nomeDoArquivoFiado(extrato),
      { type: 'application/pdf' })
    : null), [extrato, assinatura]);

  const naLoja = useMutation({
    mutationFn: async () => {
      if (!extrato) throw new Error('O extrato ainda não carregou.');
      const vias = duasVias ? (['cliente', 'loja'] as const) : (['cliente'] as const);
      // Uma folha por trabalho, na ordem: as folhas do cliente, depois as da loja.
      for (const via of vias) {
        const folhas = paginasDoExtratoHtml(extrato, { via, assinatura });
        for (let i = 0; i < folhas.length; i += 1) {
          await enfileirarImpressao({
            tipo: 'recibo',
            titulo: `Fiado — ${extrato.cliente} (via ${via === 'cliente' ? 'do cliente' : 'da loja'}`
              + `${folhas.length > 1 ? `, folha ${i + 1} de ${folhas.length}` : ''})`,
            documentoHtml: folhas[i]!,
          });
        }
      }
    },
  });

  const enviar = () => {
    if (!pdf) return;
    // share() primeiro, antes de qualquer outra coisa (a ativação do toque).
    const promessa = compartilharArquivos([pdf]);
    setResultado(null);
    setEnviando(true);
    void promessa.then((r) => { setResultado(r === 'cancelado' ? null : r); setEnviando(false); });
  };

  if (detalhes.isPending) return <Carregando texto="Lendo as vendas e os acertos" />;
  if (detalhes.error) return <Aviso tipo="erro">{mensagemDeErro(detalhes.error)}</Aviso>;

  const nada = !extrato || extrato.vendas.length === 0;
  const compartilha = pdf ? podeCompartilhar([pdf]) : false;

  return (
    <div className="space-y-3">
      <ul className="divide-y divide-edge border border-edge">
        {todas.map((v) => (
          <li key={v.saleId}>
            <label className="flex cursor-pointer items-start gap-3 px-3 py-2">
              <input type="checkbox" className="mt-1 h-4 w-4" checked={escolhidas.has(v.saleId)}
                     onChange={() => alternar(v.saleId)} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold">
                  {dataBR(v.vendida_em)} · <span className="font-mono">{v.codigo}</span>
                </span>
                <span className="block truncate text-xs text-dim">
                  {[v.veiculo, plural(v.itens.length, 'peça', 'peças')].filter(Boolean).join(' · ')}
                </span>
              </span>
              <span className="shrink-0 text-right">
                <span className="block text-sm font-bold tabular-nums">{brl(v.saldo)}</span>
                <span className={`block text-xs ${cobra(v) ? 'text-alerta' : 'text-dim'}`}>
                  {rotuloDaSituacao(v)}{v.situacao === 'parcial' ? ` · de ${brl(v.valor)}` : ''}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>

      {extrato && (
        <div className="flex items-baseline justify-between border border-edge bg-panel-alt p-3">
          <span className="text-sm">
            {plural(extrato.vendas.length, 'venda', 'vendas')} no extrato
          </span>
          <span className="text-right">
            <span className="block text-xs text-dim">em aberto</span>
            <span className="text-xl font-black tabular-nums">{brl(extrato.saldo)}</span>
          </span>
        </div>
      )}

      <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={duasVias}
                 onChange={(e) => setDuasVias(e.target.checked)} />
          Imprimir 2 vias (cliente e loja)
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={assinatura}
                 onChange={(e) => { setAssinatura(e.target.checked); setResultado(null); }} />
          Linhas para assinar
        </label>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button type="button" className="botao-primario py-3"
                disabled={nada || naLoja.isPending}
                onClick={() => naLoja.mutate()}>
          {naLoja.isPending ? 'Mandando…' : naLoja.isSuccess ? '✓ Mandado' : 'Imprimir na loja'}
        </button>
        <button type="button" className="botao-secundario py-3" disabled={nada}
                onClick={() => extrato && imprimirAqui(extratoParaImprimirAqui(extrato,
                  { vias: duasVias ? 2 : 1, assinatura }))}>
          Imprimir aqui
        </button>
        {pdf && compartilha && (
          <button type="button" className="botao-secundario py-3" disabled={enviando}
                  onClick={enviar}>
            Enviar PDF
          </button>
        )}
        <button type="button" className={`botao-secundario py-3 ${pdf && compartilha ? '' : 'col-span-2'}`}
                disabled={nada} onClick={() => pdf && baixarArquivo(pdf, pdf.name)}>
          Salvar PDF
        </button>
      </div>

      {naLoja.isSuccess && (
        <Aviso tipo="ok">
          Mandado para a impressora da loja{duasVias ? ' — 2 vias, cliente e loja assinam as duas' : ''}.
        </Aviso>
      )}
      {naLoja.error ? <Aviso tipo="erro">{mensagemDeErro(naLoja.error)}</Aviso> : null}
      {resultado === 'enviado' && <Aviso tipo="ok">Pronto: o PDF foi para o app que você escolheu.</Aviso>}
      {resultado === 'toque-de-novo' && (
        <Aviso tipo="alerta">O celular pediu um toque novo: toque em Enviar PDF de novo.</Aviso>
      )}
      {resultado === 'falhou' && (
        <Aviso tipo="erro">O Compartilhar não abriu. Salve o PDF e anexe no WhatsApp.</Aviso>
      )}
      <p className="text-xs text-dim">
        O PDF é o mesmo extrato, numa via só — é o que vai para o WhatsApp da oficina. "Imprimir aqui"
        abre a impressão deste aparelho; no computador, dá para escolher "Salvar como PDF" ali.
      </p>
    </div>
  );
}
